// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React FOUNDATION dashboard hook.
//
// A small loading/error/data hook over fetchDashboardData() so CD's redesigned
// Dashboard can drop in `const { data, loading, error } = useDashboardData();`
// without re-implementing the fetch orchestration. Design-independent, it
// returns the typed models, not any markup.
// ─────────────────────────────────────────────────────────────────────────────

import { fetchDashboardData, fetchDashboardDataFast, type DashboardData } from './dashboard';
import { useCachedData } from './dataCache';

/** Async state shape for the dashboard load. */
export interface DashboardState {
  readonly data: DashboardData | null;
  readonly loading: boolean;
  /** fast/full split: TRUE until the full aggregation lands. Sections fed by
      the slow flight (release, merch, stats) must not render empty-states
      while this is set — the fast flight lacks their data by design. */
  readonly fullLoading: boolean;
  readonly error: Error | null;
  /** Force a background refetch (e.g. after an opportunity add/edit/delete). */
  readonly reload: () => void;
}

/**
 * Fetch all dashboard sections on mount.
 * Returns `{ data, loading, error }`. Re-runs when `deps` change (e.g. pass the
 * current band id so a roster band switch refetches, Bible 06.4·C "switch
 * artist is a full teardown").
 */
export function useDashboardData(deps: ReadonlyArray<unknown> = []): DashboardState {
  // Band-scoped stale-while-revalidate: instant on tab switch, always refetches
  // in the background; server stays the source of truth.
  //
  // boot-perf 2026-07-14 (Dev request): TWO cached flights instead of one.
  // 'dashboard-fast' resolves on identity + quick own-D1 reads (~1.4s) so the
  // boot mask hides and the shell paints; 'dashboard' is the unchanged full
  // load (slow third-party aggregations included) that replaces it when it
  // lands (~3s). The two fetchers share in-flight memos in lib/dashboard.ts,
  // so this costs ZERO duplicate requests. Full data always wins once present.
  const fast = useCachedData<DashboardData>('dashboard-fast', fetchDashboardDataFast, deps);
  const full = useCachedData<DashboardData>('dashboard', fetchDashboardData, deps);
  const data = full.data ?? fast.data ?? null;
  return {
    data,
    loading: data === null && (fast.loading || full.loading),
    fullLoading: full.loading,
    error: data !== null ? null : (full.error ?? fast.error),
    reload: () => { fast.reload(); full.reload(); },
  };
}
